import {useState} from "react";
import {BarChart3, ChevronDown, MessageSquareText} from "lucide-react";
import * as m from "@/paraglide/messages";
import type {ChartWidget, PostWidget, SourceWidgets} from "@/lib/chat-runtime";
import {useToggleFavoriteWidget} from "@/hooks/use-widgets";
import {ChartCard} from "./chart-card";
import {SocialPostCard} from "./social-post-card";
import {Collapsible, CollapsibleContent, CollapsibleTrigger} from "@/components/ui/collapsible";

interface SourcesSectionProps {
  widgets: SourceWidgets;
}

// Maps original widget ID -> saved favorite/widget IDs
interface FavoriteMapping {
  favoriteId: string;
  savedWidgetId: string;
}

export function SourcesSection({widgets}: SourcesSectionProps) {
  const toggleFavorite = useToggleFavoriteWidget();
  const [favoriteMap, setFavoriteMap] = useState<Map<string, FavoriteMapping>>(new Map());

  const handleToggleFavorite = (widget: PostWidget | ChartWidget, widgetType: "post" | "chart") => {
    const existing = favoriteMap.get(widget.id);

    toggleFavorite.mutate(
      {
        widget,
        widgetType,
        existingFavoriteId: existing?.favoriteId,
        existingSavedWidgetId: existing?.savedWidgetId,
      },
      {
        onSuccess: (result) => {
          setFavoriteMap((prev) => {
            const next = new Map(prev);
            if (result.action === "added" && result.favoriteId && result.savedWidgetId) {
              next.set(widget.id, {
                favoriteId: result.favoriteId,
                savedWidgetId: result.savedWidgetId,
              });
            } else {
              next.delete(widget.id);
            }
            return next;
          });
        },
      }
    );
  };

  const isWidgetFavorited = (widget: PostWidget | ChartWidget) => favoriteMap.has(widget.id);
  // Count total widgets across all types
  const totalCount = (widgets.post?.length || 0) + (widgets.chart?.length || 0);
  const hasCharts = (widgets.chart?.length || 0) > 0;

  if (totalCount === 0) return null;

  const Icon = hasCharts ? BarChart3 : MessageSquareText;
  const label = hasCharts ? m.sources_section_visualization() : m.sources_section_sources();

  return (
    <Collapsible defaultOpen={true} className="mt-4">
      <CollapsibleTrigger className="group hover:bg-muted -mx-2 flex !cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors select-none">
        <div className="flex h-5 w-5 items-center justify-center rounded-md bg-gradient-to-br from-blue-500/10 to-purple-500/10 dark:from-blue-500/20 dark:to-purple-500/20">
          <Icon className="h-3 w-3 text-blue-600 dark:text-blue-400" />
        </div>
        <span className="text-foreground font-medium">{label}</span>
        {!hasCharts && (
          <span className="bg-muted text-muted-foreground flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-medium">
            {totalCount}
          </span>
        )}
        <ChevronDown className="text-muted-foreground ml-auto h-4 w-4 transition-transform duration-200 group-data-[state=open]:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-3 flex flex-col gap-3">
          {/* Render charts first (more prominent) */}
          {widgets.chart?.map((widget) => (
            <ChartCard
              key={widget.id}
              chart={widget}
              isFavorited={isWidgetFavorited(widget)}
              onFavoriteClick={() => handleToggleFavorite(widget, "chart")}
            />
          ))}

          {/* Render social media posts */}
          {widgets.post?.map((widget) => (
            <SocialPostCard
              key={widget.id}
              post={widget}
              isFavorited={isWidgetFavorited(widget)}
              onFavoriteClick={() => handleToggleFavorite(widget, "post")}
            />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
